import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import { makeStyles } from "@material-ui/core/styles";
import Modal from "@material-ui/core/Modal";
import Fade from "@material-ui/core/Fade";

import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import AddIcon from "@material-ui/icons/Add";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import TableConfigurator from "../TableLayout/TableConfigurator";
import TableSelectorCell from "./TableSelectorCell";
import TableSelectModal from "./TableSelectModal";

import { defaultPasscodeTheme } from "../../../../constants/passCodeUtils";
import { setTableLayout } from "../../../../actions/projectmail";
import { defaultTableLayout } from "../../../../reducers/projectmail";

const useStyle = makeStyles(theme => ({
  ...defaultPasscodeTheme(theme),
  inline: {
    display: "flex",
    maxWidth: "100%",
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    width: "850px",
    backgroundColor: "#eee",
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(3, 4, 3),
  },
  add: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cell: {
    paddingTop: theme.spacing(1),
  },
  spacer: {
    flexGrow: 1,
  },
  paddingBottom: {
    paddingBottom: theme.spacing(2),
  },
  selectContainer: {
    maxHeight: "600px",
    overflowY: "scroll",
    "&::-webkit-scrollbar": { display: "none" },
    "&-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },
  padding: {
    padding: theme.spacing(0, 2),
    margin: theme.spacing(0, 1),
  },
}));

export const TableSelectorComponent = ({ tableLayout, type, translate, open, onClose, setTableLayoutFn }) => {
  const classes = useStyle();
  const [ownTableLayout, setOwnTableLayout] = useState(new Array(...tableLayout[type]));
  const [openSelect, setOpenSelect] = useState(false);

  useEffect(() => {
    if (ownTableLayout.length === 0) setOwnTableLayout(new Array(...tableLayout[type]));
  }, [setOwnTableLayout, ownTableLayout, tableLayout, type]);

  const onClickDelete = dItem => {
    const newArray = ownTableLayout.filter(item => item !== dItem);
    setOwnTableLayout(newArray);
  };

  const onClickUp = (item, idx) => {
    const newArray = new Array(...ownTableLayout);
    const temp = newArray[idx - 1];
    newArray[idx] = temp;
    newArray[idx - 1] = item;
    setOwnTableLayout(newArray);
  };

  const onClickDown = (item, idx) => {
    const newArray = new Array(...ownTableLayout);
    const temp = newArray[idx + 1];
    newArray[idx] = temp;
    newArray[idx + 1] = item;
    setOwnTableLayout(newArray);
  };

  return [
    <Modal
      key="selector"
      open={open}
      className={classes.modal}
      onClose={() => {
        onClose();
        setOwnTableLayout([]);
      }}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          <div className={classes.selectContainer}>
            {ownTableLayout.map((item, idx) => (
              <div className={classes.cell} key={"selectorcell " + item}>
                <TableSelectorCell
                  value={translate[TableConfigurator[item].label]}
                  onClickDelete={idx > 5 ? () => onClickDelete(item) : undefined}
                  onClickUp={idx > 6 ? () => onClickUp(item, idx) : undefined}
                  onClickDown={ownTableLayout.length !== idx + 1 && idx > 5 ? () => onClickDown(item, idx) : undefined}
                />
              </div>
            ))}
            <div className={classes.paddingBottom} />
          </div>
          <Card>
            <CardContent>
              <div className={`${classes.add}`}>
                <Tooltip title="Add Element">
                  <IconButton aria-label="add" onClick={() => setOpenSelect(true)}>
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </CardContent>
          </Card>
          <div className={classes.paddingBottom} />
          <div className={`${classes.inline}`}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                onClose();
                setOwnTableLayout([]);
              }}
            >
              {translate.backButton}
            </Button>
            <Button
              variant="contained"
              className={classes.padding}
              onClick={() => {
                setTableLayoutFn(defaultTableLayout, type);
                onClose();
                setOwnTableLayout([]);
              }}
            >
              {translate.reset}
            </Button>
            <div className={classes.spacer} />
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setTableLayoutFn(ownTableLayout, type);
                onClose();
              }}
            >
              {translate.saveButton}
            </Button>
          </div>
        </div>
      </Fade>
    </Modal>,
    <TableSelectModal
      key="select-modal"
      onClick={item => {
        if (ownTableLayout.indexOf(item) === -1) {
          const newArray = new Array(...ownTableLayout);
          newArray.push(item);
          setOwnTableLayout(newArray);
        } else setOwnTableLayout(ownTableLayout.filter(element => item !== element));
      }}
      open={openSelect}
      onClose={() => setOpenSelect(false)}
      ownTableLayout={ownTableLayout}
    />,
  ];
};

TableSelectorComponent.propTypes = {
  tableLayout: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  translate: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  setTableLayoutFn: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
};

const mapStateToProps = state => ({
  tableLayout: state.projectmail.tableLayout,
  translate: state.ui.translate,
});

const mapDispatchToProps = dispatch => ({
  setTableLayoutFn: (tableLayout, type) => dispatch(setTableLayout(tableLayout, type)),
});

const TableSelector = connect(
  mapStateToProps,
  mapDispatchToProps
)(TableSelectorComponent);

export default TableSelector;
